import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  addDoc
} from 'firebase/firestore';
import { db } from './config';

// ==================== ORGANIZATIONS ====================

export const createOrganization = async (userId, orgData) => {
  try {
    const orgRef = doc(collection(db, 'organizations'));
    const organization = {
      name: orgData.name,
      domains: orgData.domains || [],
      industry: orgData.industry,
      platforms: orgData.platforms || ['chatgpt', 'gemini', 'claude', 'perplexity'],
      competitors: orgData.competitors || [],
      createdBy: userId,
      members: [userId],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    await setDoc(orgRef, organization);

    // Update user document with organizationId
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      organizationId: orgRef.id,
      updatedAt: serverTimestamp()
    });

    return { id: orgRef.id, ...organization };
  } catch (error) {
    console.error('Error creating organization:', error);
    throw error;
  }
};

export const getOrganization = async (orgId) => {
  try {
    const orgRef = doc(db, 'organizations', orgId);
    const orgSnap = await getDoc(orgRef);

    if (orgSnap.exists()) {
      return { id: orgSnap.id, ...orgSnap.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting organization:', error);
    throw error;
  }
};

export const updateOrganization = async (orgId, updates) => {
  try {
    const orgRef = doc(db, 'organizations', orgId);
    await updateDoc(orgRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating organization:', error);
    throw error;
  }
};

// ==================== SEARCH TERMS ====================

export const createSearchTerm = async (organizationId, termData) => {
  try {
    const termRef = collection(db, 'searchTerms');
    const searchTerm = {
      organizationId,
      term: termData.term,
      active: termData.active !== undefined ? termData.active : true,
      platforms: termData.platforms || ['chatgpt', 'gemini', 'claude', 'perplexity'],
      addedBy: termData.userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(termRef, searchTerm);
    return { id: docRef.id, ...searchTerm };
  } catch (error) {
    console.error('Error creating search term:', error);
    throw error;
  }
};

export const getSearchTerms = async (organizationId) => {
  try {
    const termsRef = collection(db, 'searchTerms');
    const q = query(
      termsRef,
      where('organizationId', '==', organizationId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const terms = [];
    querySnapshot.forEach((doc) => {
      terms.push({ id: doc.id, ...doc.data() });
    });

    return terms;
  } catch (error) {
    console.error('Error getting search terms:', error);
    throw error;
  }
};

export const updateSearchTerm = async (termId, updates) => {
  try {
    const termRef = doc(db, 'searchTerms', termId);
    await updateDoc(termRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating search term:', error);
    throw error;
  }
};

export const deleteSearchTerm = async (termId) => {
  try {
    const termRef = doc(db, 'searchTerms', termId);
    await deleteDoc(termRef);
  } catch (error) {
    console.error('Error deleting search term:', error);
    throw error;
  }
};

export const toggleSearchTermStatus = async (termId, active) => {
  try {
    await updateSearchTerm(termId, { active });
  } catch (error) {
    console.error('Error toggling search term status:', error);
    throw error;
  }
};

// ==================== COMPETITORS ====================

export const createCompetitor = async (organizationId, competitorData) => {
  try {
    const competitorRef = collection(db, 'competitors');
    const competitor = {
      organizationId,
      name: competitorData.name,
      domain: competitorData.domain,
      website: competitorData.website || '',
      active: competitorData.active !== undefined ? competitorData.active : true,
      addedBy: competitorData.userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(competitorRef, competitor);
    return { id: docRef.id, ...competitor };
  } catch (error) {
    console.error('Error creating competitor:', error);
    throw error;
  }
};

export const getCompetitors = async (organizationId) => {
  try {
    const competitorsRef = collection(db, 'competitors');
    const q = query(
      competitorsRef,
      where('organizationId', '==', organizationId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const competitors = [];
    querySnapshot.forEach((doc) => {
      competitors.push({ id: doc.id, ...doc.data() });
    });

    return competitors;
  } catch (error) {
    console.error('Error getting competitors:', error);
    throw error;
  }
};

export const updateCompetitor = async (competitorId, updates) => {
  try {
    const competitorRef = doc(db, 'competitors', competitorId);
    await updateDoc(competitorRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating competitor:', error);
    throw error;
  }
};

export const deleteCompetitor = async (competitorId) => {
  try {
    const competitorRef = doc(db, 'competitors', competitorId);
    await deleteDoc(competitorRef);
  } catch (error) {
    console.error('Error deleting competitor:', error);
    throw error;
  }
};

// ==================== RANKINGS ====================

export const getRankings = async (organizationId, searchTermId = null) => {
  try {
    const rankingsRef = collection(db, 'rankings');
    let q;

    if (searchTermId) {
      q = query(
        rankingsRef,
        where('organizationId', '==', organizationId),
        where('searchTermId', '==', searchTermId),
        orderBy('checkedAt', 'desc')
      );
    } else {
      q = query(
        rankingsRef,
        where('organizationId', '==', organizationId),
        orderBy('checkedAt', 'desc')
      );
    }

    const querySnapshot = await getDocs(q);
    const rankings = [];
    querySnapshot.forEach((doc) => {
      rankings.push({ id: doc.id, ...doc.data() });
    });

    return rankings;
  } catch (error) {
    console.error('Error getting rankings:', error);
    throw error;
  }
};

// ==================== HELPER FUNCTIONS ====================

export const getUserOrganization = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists() && userSnap.data().organizationId) {
      const organization = await getOrganization(userSnap.data().organizationId);
      return organization;
    }
    return null;
  } catch (error) {
    console.error('Error getting user organization:', error);
    throw error;
  }
};

// ==================== AI SUGGESTIONS (Placeholder) ====================

export const getSuggestedSearchTerms = async (industry, domain) => {
  // This is a placeholder. In production, this would call a Cloud Function
  // that uses OpenAI or Firebase AI to generate suggestions

  // For now, return industry-specific suggestions
  const suggestions = {
    'Technology': [
      'best {product_type} software',
      'top {product_type} platforms',
      '{product_type} comparison',
      'enterprise {product_type} solutions',
      'AI-powered {product_type} tools'
    ],
    'Finance': [
      'best financial planning software',
      'top investment platforms',
      'financial advisory services',
      'wealth management solutions',
      'fintech innovations'
    ],
    'Healthcare': [
      'best healthcare software',
      'medical practice management',
      'patient care solutions',
      'healthcare technology platforms',
      'telemedicine services'
    ],
    'Retail': [
      'best e-commerce platforms',
      'retail management software',
      'inventory management solutions',
      'point of sale systems',
      'customer loyalty programs'
    ]
  };

  return suggestions[industry] || [
    'best {industry} software',
    'top {industry} solutions',
    '{industry} service providers',
    'enterprise {industry} platforms',
    'AI-powered {industry} tools'
  ];
};

export const getSuggestedCompetitors = async (industry, domain) => {
  // This is a placeholder. In production, this would call a Cloud Function
  // that uses OpenAI or Firebase AI to identify competitors

  // For now, return empty array - will be populated by Cloud Functions
  return [];
};

// ==================== LLMS.TXT ANALYSES ====================

export const saveLlmsTxtAnalysis = async (organizationId, analysisData) => {
  try {
    const analysisRef = collection(db, 'llmsTxtAnalyses');
    const analysis = {
      organizationId,
      domains: analysisData.domains,
      summary: analysisData.summary,
      suggestedLlmsTxt: analysisData.suggestedLlmsTxt,
      analyzedBy: analysisData.userId,
      createdAt: serverTimestamp()
    };

    const docRef = await addDoc(analysisRef, analysis);
    return { id: docRef.id, ...analysis };
  } catch (error) {
    console.error('Error saving llms.txt analysis:', error);
    throw error;
  }
};

export const getLlmsTxtAnalyses = async (organizationId, limit = 20) => {
  try {
    const analysesRef = collection(db, 'llmsTxtAnalyses');
    const q = query(
      analysesRef,
      where('organizationId', '==', organizationId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const analyses = [];
    let count = 0;
    querySnapshot.forEach((doc) => {
      if (count < limit) {
        analyses.push({ id: doc.id, ...doc.data() });
        count++;
      }
    });

    return analyses;
  } catch (error) {
    console.error('Error getting llms.txt analyses:', error);
    throw error;
  }
};

export const getLlmsTxtAnalysisById = async (analysisId) => {
  try {
    const analysisRef = doc(db, 'llmsTxtAnalyses', analysisId);
    const analysisSnap = await getDoc(analysisRef);

    if (analysisSnap.exists()) {
      return { id: analysisSnap.id, ...analysisSnap.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting llms.txt analysis:', error);
    throw error;
  }
};

export const deleteLlmsTxtAnalysis = async (analysisId) => {
  try {
    const analysisRef = doc(db, 'llmsTxtAnalyses', analysisId);
    await deleteDoc(analysisRef);
  } catch (error) {
    console.error('Error deleting llms.txt analysis:', error);
    throw error;
  }
};

export const getDomainScoreHistory = async (organizationId, domain) => {
  try {
    const analysesRef = collection(db, 'llmsTxtAnalyses');
    const q = query(
      analysesRef,
      where('organizationId', '==', organizationId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const history = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const domainResult = data.domains?.find(d => d.domain === domain);
      if (domainResult) {
        history.push({
          id: doc.id,
          date: data.createdAt,
          score: domainResult.analysis?.overallScore || 0,
          hasLlmsTxt: domainResult.hasLlmsTxt,
          formatScore: domainResult.analysis?.formatCompliance?.score || 0,
          contentScore: domainResult.analysis?.contentQuality?.score || 0,
          geoScore: domainResult.analysis?.geoOptimization?.score || 0
        });
      }
    });

    return history;
  } catch (error) {
    console.error('Error getting domain score history:', error);
    throw error;
  }
};
